import React from 'react';
import { Button } from '@/components/ui/button.tsx';
import { PlusIcon } from 'lucide-react';
import { ExpandedState, Row, RowData } from '@tanstack/react-table';
import {
  collectDeviceSns,
  createDefaultDevice,
  TDevice,
  TTreeDevice,
} from '@/features/devices/schemas/devices.schemas.ts';
import { useTranslation } from 'react-i18next';
import {
  appendChildAtPath,
  buildSuffixes,
  parseRowPath,
} from '@/features/devices/components/device-tree-table/device-tree-table.utils.ts';

export const DeviceTableToolbarAddButton = ({
  data,
  deviceInfo,
  getSelectedTableRow,
  setData,
  setExpandedRows,
}: {
  data: TTreeDevice[];
  deviceInfo: {
    plantId: string;
    timezone: string;
  };
  getSelectedTableRow: () => Row<RowData> | undefined;
  setData: (devices: TTreeDevice[]) => void;
  setExpandedRows: React.Dispatch<React.SetStateAction<ExpandedState>>;
}) => {
  const { t } = useTranslation();

  const createNewDevice = (): TDevice => {
    const existingSns = collectDeviceSns(data);
    let identifiers = buildSuffixes('new');
    while (existingSns.has(identifiers.sn)) {
      identifiers = buildSuffixes('new');
    }

    return {
      ...createDefaultDevice(deviceInfo),
      name: identifiers.name,
      sn: identifiers.sn,
    };
  };

  const handleAddDevice = () => {
    const selectedRow = getSelectedTableRow();
    const newDevice = createNewDevice();

    if (!selectedRow) {
      setData([...data, newDevice]);
      return;
    }

    const parentPath = parseRowPath(selectedRow.id);
    setData(appendChildAtPath(data, parentPath, newDevice));

    setExpandedRows((prev) => {
      if (prev === true) return true;
      return { ...prev, [selectedRow.id]: true };
    });
  };

  return (
    <Button
      type="button"
      onClick={handleAddDevice}
      variant="ghost"
      className="flex items-center gap-2 rounded-none px-2 py-5 font-normal hover:bg-gray-200 sm:px-3"
    >
      <PlusIcon className="text-muted-foreground size-4" />
      <span>{t('common.buttons.addButton')}</span>
    </Button>
  );
};
